import GiftDialog from "./GiftDialog"
import type { ItemInput } from "../types"
interface Props {
  initialValues?: Partial<ItemInput>
  onSubmit: (item: ItemInput) => Promise<unknown>
  onCancel: () => void
  submitLabel: string
}
import type { FormEvent } from "react"
import { errorMessage } from "../types"
import { useRef, useState } from "react"
import ItemImage from "./ItemImage"
import ImagePicker from "./ImagePicker"
import { useProductPreview } from "../hooks/useProductPreview"

const INPUT_CLASSES =
  "w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"

const PREVIEW_HINTS: Record<string, string> = {
  missed: "We couldn't find details for that link, so fill them in below.",
  busy: "Product lookups are busy right now, so try again in a moment or fill in the details below.",
}
const DEFAULT_PREVIEW_HINT =
  "Paste a link and we'll fill in the name and picture"

export default function ItemForm({
  initialValues = {},
  onSubmit,
  onCancel,
  submitLabel,
}: Props) {
  const firstField = useRef<HTMLInputElement>(null)
  const {
    name,
    setName,
    url,
    setUrl,
    imageUrl,
    setImageUrl,
    previewStatus,
    handleUrlBlur,
    retry,
    typedIn,
  } = useProductPreview(initialValues)
  const [pictureSearch, setPictureSearch] = useState<
    "unopened" | "open" | "closed"
  >("unopened")
  const [notes, setNotes] = useState(initialValues.notes || "")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await onSubmit({
        name: name.value,
        url: url || null,
        image_url: imageUrl.value || null,
        notes: notes || null,
      })
    } catch (err) {
      setError(errorMessage(err))
      setLoading(false)
    }
  }

  const editorTitle = submitLabel === "Save changes" ? "Edit gift" : "Add a gift"
  return (
    <GiftDialog title={pictureSearch === "open" ? "Find a picture" : editorTitle}
      onBack={pictureSearch === "open" ? () => setPictureSearch("closed") : undefined}
      busy={loading}
      dirty={name.value !== (initialValues.name || "") || url !== (initialValues.url || "") || imageUrl.value !== (initialValues.image_url || "") || notes !== (initialValues.notes || "")}
      onClose={onCancel}>
    <form hidden={pictureSearch === "open"}
      onSubmit={(event) => {
        void handleSubmit(event)
      }}
      className="bg-gray-50 rounded-lg p-4 space-y-3"
    >
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      <fieldset disabled={loading} className="space-y-3">
      <legend className="sr-only">{editorTitle}</legend>
      <div>
        <label
          htmlFor="item-url"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Product link{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          ref={firstField}
          id="item-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={handleUrlBlur}
          aria-describedby="item-url-hint"
          className={INPUT_CLASSES}
        />
        <p
          id="item-url-hint"
          className="text-sm text-gray-500 mt-1"
          aria-live="polite"
        >
          {previewStatus === "looking" ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-3 h-3 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
                aria-hidden="true"
              />
              Looking up product…
            </span>
          ) : (
            PREVIEW_HINTS[previewStatus || ""] || DEFAULT_PREVIEW_HINT
          )}
        </p>
        {previewStatus === "busy" && (
          <button
            type="button"
            onClick={() => {
              void retry()
            }}
            className="min-h-11 min-w-11 px-3 text-sm text-blue-600 hover:text-blue-800 font-medium mt-1 transition-colors"
          >
            Try again
          </button>
        )}
      </div>
      <div>
        <label
          htmlFor="item-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Name
        </label>
        <input
          id="item-name"
          type="text"
          value={name.value}
          onChange={(e) => setName(typedIn(e.target.value))}
          required
          className={INPUT_CLASSES}
        />
      </div>
      <div>
        <label
          htmlFor="item-image-url"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Picture link{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-40 h-40 [&>div]:w-full [&>div]:h-full"><ItemImage src={imageUrl.value} /></div>
          <details className="basis-full"><summary className="min-h-11 cursor-pointer">Use an image link</summary>          <input
            id="item-image-url"
            type="url"
            value={imageUrl.value}
            onChange={(e) => setImageUrl(typedIn(e.target.value))}
            placeholder="https://"
            className={`${INPUT_CLASSES} basis-full`}
          /></details>
          {imageUrl.value && (
            <button
              type="button"
              onClick={() => setImageUrl(typedIn(""))}
              className="min-h-11 min-w-11 px-3 text-sm text-gray-500 hover:text-red-600 transition-colors shrink-0"
            >
              Remove picture
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setPictureSearch("open")}
        className="min-h-11 min-w-11 px-3 text-sm text-blue-600"
      >
        {imageUrl.value ? "Change picture" : "Find a picture"}
      </button>
      <div>
        <label
          htmlFor="item-notes"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Notes <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          id="item-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={`${INPUT_CLASSES} resize-none`}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white min-h-11 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? "Saving..." : submitLabel}
        </button>
        <button
          type="button"
          onClick={() => { document.querySelector<HTMLDialogElement>(".gift-dialog")?.dispatchEvent(new Event("cancel", { cancelable: true })) }}
          className="text-gray-500 min-h-11 px-4 py-2 rounded-lg text-sm hover:text-gray-900 transition-colors"
        >
          Cancel
        </button>
      </div>
      </fieldset>
    </form>
      {pictureSearch !== "unopened" && (
        <div hidden={pictureSearch === "closed"}>
          <ImagePicker
            initialQuery={name.value}
            onSelect={(value) => {
              setImageUrl(typedIn(value))
              setPictureSearch("closed")
            }}
            onClose={() => setPictureSearch("closed")}
          />
        </div>
      )}
    </GiftDialog>
  )
}
